import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CalendarDays,
    Clock,
    MapPin,
    Tag,
    Sparkles,
    ImageOff,
    Wrench,
    Building2,
    X,
    CheckCircle2,
    Package,
} from 'lucide-react';
import {
    fetchSugerenciasByUsuario,
    updateSugerencia,
    registrarInteraccion,
    reservarSugerencia,
    completarSugerencias,
    SugerenciaInvalidaError,
    SugerenciaCita,
} from '../../../../services/sugerenciaCitaService';

// Ajusta este import según cómo obtengas el usuario logueado en tu app
import { useAuth } from '../../../../context/AuthContext';
// Sistema de notificaciones toast (esquina superior derecha, 5s de duración)
import { useToast } from '../../../../components/common/ToastNotification';
import { useTheme } from '../../../../context/ThemeContext';
import {
    fusionarReemplazoDiverso,
    identidadFeedCoincide,
    normalizarSugerenciasDiversas,
    siguienteIdentidadFeed,
} from './suggestionFeed';

const formatFecha = (fecha: string | null) => {
    if (!fecha) return null;
    const d = new Date(`${fecha}T00:00:00`);
    return d.toLocaleDateString('es-BO', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
    });
};

const formatHora = (hora: string | null) => {
    if (!hora) return null;
    const [h, m] = hora.split(':');
    const date = new Date();
    date.setHours(Number(h), Number(m));
    return date.toLocaleTimeString('es-BO', {
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatMoneda = (valor: number | null) => {
    if (valor === null || valor === undefined) return null;
    return `Bs ${Number(valor).toFixed(2)}`;
};

const calcularPorcentajeDescuento = (subtotal: number | null, descuento: number | null) => {
    if (!subtotal || !descuento || subtotal <= 0) return null;
    return Math.round((descuento / subtotal) * 100);
};

const INTERVALO_CAMBIO_MS = 3200;
const UMBRAL_TIEMPO_VISTO_MS = 400; // por debajo de esto es "pasó de largo", no cuenta como vista real
const DURACION_ANIMACION_SALIDA_MS = 320; // debe coincidir con la duration-300/320 usada en las clases de la tarjeta

/**
 * Trackea impresión (primera vez visible >=50%) y tiempo total visible (ms)
 * de una tarjeta usando IntersectionObserver. El tiempo se acumula en
 * tramos (si el usuario scrollea, vuelve, etc.) y se envía una sola vez
 * al desmontar/salir de la lista, para no spamear al backend.
 *
 * IMPORTANTE: cuando se dispara la primera impresión real, avisamos al
 * padre vía `onImpresion` para que actualice `vista` en el estado local
 * DE INMEDIATO. El backend ya marca `vista=True` apenas recibe el evento
 * 'impresion' (ver SugerenciaCitaViewSet.interaccion), así que si no
 * sincronizamos el estado local en el momento, la tarjeta sigue mostrando
 * el badge "Nueva" en pantalla hasta el próximo refetch (por ejemplo al
 * descartar otra tarjeta) — y en ese refetch varias tarjetas "nuevas"
 * cambian de golpe a "vistas", dando la falsa impresión de que descartar
 * una tarjeta afectó a las demás. En realidad ya estaban vistas desde
 * que se mostraron en pantalla; solo estaba desincronizado el estado local.
 *
 * NOTA: esto significa que `s.vista` puede volverse `true` casi al toque
 * de montarse la tarjeta. El badge "Nueva" NO depende de `s.vista` en vivo
 * (ver `eraNuevaAlMontar` en TarjetaSugerencia) precisamente para que no
 * desaparezca antes de que el usuario llegue a verlo.
 */
function useSeguimientoSugerencia(
    sugerenciaId: number,
    yaVista: boolean,
    onImpresion: (id: number) => void
) {
    const ref = useRef<HTMLElement | null>(null);
    const visibleDesdeRef = useRef<number | null>(null);
    const acumuladoRef = useRef(0);
    const impresionEnviadaRef = useRef(yaVista);

    useEffect(() => {
        const nodo = ref.current;
        if (!nodo) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (visibleDesdeRef.current === null) {
                        visibleDesdeRef.current = Date.now();
                    }
                    if (!impresionEnviadaRef.current) {
                        impresionEnviadaRef.current = true;
                        // Sincronizamos el estado local YA, sin esperar la
                        // respuesta del backend (que de todas formas va a
                        // marcar vista=True con este mismo evento).
                        onImpresion(sugerenciaId);
                        registrarInteraccion(sugerenciaId, { tipo: 'impresion' }).catch((err) =>
                            (void 0)
                        );
                    }
                } else if (visibleDesdeRef.current !== null) {
                    acumuladoRef.current += Date.now() - visibleDesdeRef.current;
                    visibleDesdeRef.current = null;
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(nodo);

        return () => {
            observer.disconnect();
            if (visibleDesdeRef.current !== null) {
                acumuladoRef.current += Date.now() - visibleDesdeRef.current;
                visibleDesdeRef.current = null;
            }
            const total = Math.round(acumuladoRef.current);
            if (total >= UMBRAL_TIEMPO_VISTO_MS) {
                registrarInteraccion(sugerenciaId, {
                    tipo: 'tiempo_visto',
                    tiempo_visto_ms: total,
                }).catch((err) => (void 0));
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sugerenciaId]);

    return ref;
}

const CardBanner: React.FC<{
    servicioImg: string | null;
    servicioNombre: string;
    espacioImg: string | null;
    espacioNombre: string | null;
}> = ({ servicioImg, servicioNombre, espacioImg, espacioNombre }) => {
    const slides = useMemo(
        () =>
            [
                servicioImg
                    ? { src: servicioImg, label: servicioNombre, tipo: 'servicio' as const }
                    : null,
                espacioImg
                    ? { src: espacioImg, label: espacioNombre || '', tipo: 'espacio' as const }
                    : null,
            ].filter(Boolean) as {
                src: string;
                label: string;
                tipo: 'servicio' | 'espacio';
            }[],
        [servicioImg, servicioNombre, espacioImg, espacioNombre]
    );

    const [index, setIndex] = useState(0);

    useEffect(() => {
        setIndex(0);
        if (slides.length < 2) return;
        const id = setInterval(() => {
            setIndex((prev) => (prev + 1) % slides.length);
        }, INTERVALO_CAMBIO_MS);
        return () => clearInterval(id);
    }, [slides.length, servicioImg, espacioImg]);

    if (slides.length === 0) {
        return (
            <div className="relative w-full aspect-[4/3] rounded-2xl bg-rose-50 border border-rose-100 flex flex-col items-center justify-center gap-2">
                <ImageOff className="w-6 h-6 text-rose-300" strokeWidth={1.6} />
                <span className="text-[11px] font-medium text-rose-300">
                    Sin imagen disponible
                </span>
            </div>
        );
    }

    return (
        <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-rose-50 border border-rose-100">
            {slides.map((slide, i) => (
                <img
                    key={slide.tipo}
                    src={slide.src}
                    alt={slide.label}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${i === index ? 'opacity-100' : 'opacity-0'
                        }`}
                />
            ))}
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/55 to-transparent" />
            <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1.5 bg-black/35 backdrop-blur-sm text-white text-[11px] font-semibold px-2.5 py-1 rounded-full">
                {slides[index].tipo === 'servicio' ? (
                    <Wrench className="w-3 h-3" strokeWidth={2} />
                ) : (
                    <Building2 className="w-3 h-3" strokeWidth={2} />
                )}
                {slides[index].label}
            </div>
            {slides.length > 1 && (
                <div className="absolute top-2.5 right-2.5 flex gap-1">
                    {slides.map((slide, i) => (
                        <span
                            key={slide.tipo}
                            className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? 'w-5 bg-white' : 'w-1.5 bg-white/50'
                                }`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Tarjeta individual de sugerencia. Extraída a su propio componente para
 * poder usar el hook de tracking por tarjeta (los hooks no pueden vivir
 * dentro de un .map()).
 */
const TarjetaSugerencia: React.FC<{
    s: SugerenciaCita;
    saliendo: boolean;
    onMarcarVista: (id: number) => void;
    onImpresion: (id: number) => void;
    onAbrirModal: (s: SugerenciaCita) => void;
    onDescartar: (s: SugerenciaCita) => void;
}> = ({ s, saliendo, onMarcarVista, onImpresion, onAbrirModal, onDescartar }) => {
    const { isDark } = useTheme();
    const ref = useSeguimientoSugerencia(s.id, s.vista, onImpresion);
    const yaAdquirida = Boolean(s.cita_referencia || s.solicitud_reserva);

    // El badge "Nueva" se decide UNA sola vez, al montar la tarjeta, y ya
    // no cambia aunque `s.vista` pase a true casi de inmediato por el
    // tracking de impresión (ver comentario en useSeguimientoSugerencia).
    // Así el usuario sí alcanza a verlo en la esquina superior.
    const eraNuevaAlMontar = useRef(!s.vista).current;

    // Animación de entrada: fade + scale sutil al aparecer, tanto en la
    // carga inicial como cuando esta tarjeta ocupa el slot que dejó una
    // sugerencia descartada/reservada.
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const frame = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    return (
        <article
            ref={ref as React.RefObject<HTMLElement>}
            className={`relative flex flex-col gap-3.5 rounded-[20px] border p-4 shadow-[0_4px_14px_rgba(20,10,30,0.04)] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_14px_30px_rgba(97,37,123,0.18)] ${
                isDark
                    ? (!s.vista ? 'border-purple-500/35 bg-[rgba(26,12,48,0.85)] text-white backdrop-blur-md' : 'border-purple-500/15 bg-[rgba(22,10,43,0.85)] text-white backdrop-blur-md')
                    : (!s.vista ? 'border-purple-200 bg-gradient-to-b from-purple-50/70 to-white text-slate-800' : 'border-purple-100/70 bg-white text-slate-800')
                } ${saliendo ? 'pointer-events-none' : ''} ${saliendo
                    ? 'opacity-0 scale-90 -rotate-1'
                    : visible
                        ? 'opacity-100 scale-100'
                        : 'opacity-0 scale-95'
                }`}
        >
            {eraNuevaAlMontar && !yaAdquirida && (
                <span className="absolute top-6 right-6 z-10 bg-gradient-to-br from-[#61257B] to-[#C1348B] text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow">
                    Nueva
                </span>
            )}

            {yaAdquirida && (
                <span className="absolute top-6 right-6 z-10 flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold px-2.5 py-1 rounded-full">
                    <CheckCircle2 className="w-3 h-3" strokeWidth={2.4} />
                    Solicitud enviada
                </span>
            )}

            {/* Banner de imagen (servicio / espacio) */}
            <CardBanner
                servicioImg={s.servicio_sugerido_imagen}
                servicioNombre={s.servicio_sugerido_nombre || 'Servicio'}
                espacioImg={s.espacio_servicio_imagen}
                espacioNombre={s.espacio_servicio_nombre}
            />

            <div className="flex items-center justify-between px-1">
                <span className="text-[11.5px] font-bold text-rose-700 bg-rose-50 px-2.5 py-1 rounded-full">
                    {s.categoria_nombre || 'Sugerencia'}
                </span>
                {s.porcentaje_descuento_promocional != null || s.descuento_sugerido ? (
                    <span className="text-[11.5px] font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
                        -{s.porcentaje_descuento_promocional != null
                            ? Number(s.porcentaje_descuento_promocional).toFixed(0)
                            : calcularPorcentajeDescuento(s.subtotal, s.descuento_sugerido)}% dto.
                    </span>
                ) : null}
            </div>

            <div className="px-1">
                <h3 className="text-[15px] font-extrabold text-slate-900 leading-snug">
                    {s.servicio_sugerido_nombre || 'Servicio sugerido'}
                </h3>
                {s.plan_nombre && (
                    <p className="text-[12.5px] text-slate-400 font-medium">
                        {s.plan_nombre}
                    </p>
                )}
            </div>

            <div className="flex flex-col gap-2 px-1">
                {s.fecha_inicio && (
                    <div className="flex items-center gap-2 text-[13.5px] text-slate-600">
                        <CalendarDays className="w-4 h-4 text-rose-400" strokeWidth={1.9} />
                        <span>
                            {formatFecha(s.fecha_inicio)}
                            {s.fecha_fin && s.fecha_fin !== s.fecha_inicio
                                ? ` – ${formatFecha(s.fecha_fin)}`
                                : ''}
                        </span>
                    </div>
                )}
                {s.hora_inicio && (
                    <div className="flex items-center gap-2 text-[13.5px] text-slate-600">
                        <Clock className="w-4 h-4 text-rose-400" strokeWidth={1.9} />
                        <span>
                            {formatHora(s.hora_inicio)} - {formatHora(s.hora_fin)}
                        </span>
                    </div>
                )}
                {s.espacio_servicio_nombre && (
                    <div className="flex items-center gap-2 text-[13.5px] text-slate-600">
                        <MapPin className="w-4 h-4 text-rose-400" strokeWidth={1.9} />
                        <span>{s.espacio_servicio_nombre}</span>
                    </div>
                )}
                {s.cupon_codigo && (
                    <div className="flex items-center gap-2 text-[13.5px] text-slate-600">
                        <Tag className="w-4 h-4 text-rose-400" strokeWidth={1.9} />
                        <span>Cupón {s.cupon_codigo}</span>
                    </div>
                )}
            </div>

            {s.motivo && (
                <p className="mx-1 text-[13px] italic text-rose-900/60 bg-rose-50/60 border-l-[3px] border-rose-200 px-3 py-2 rounded-lg">
                    "{s.motivo}"
                </p>
            )}

            <div className="mt-auto pt-3.5 mx-1 border-t border-dashed border-rose-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex flex-col">
                    {s.total ? (
                        <>
                            <span className="text-lg font-extrabold text-rose-600">
                                {formatMoneda(s.total)}
                            </span>
                            {s.subtotal && s.subtotal !== s.total && (
                                <span className="text-xs text-slate-300 line-through">
                                    {formatMoneda(s.subtotal)}
                                </span>
                            )}
                        </>
                    ) : (
                        <span className="text-lg font-extrabold text-rose-600">
                            Consultar
                        </span>
                    )}
                </div>

                <div className="flex gap-2">
                    {!s.vista && !yaAdquirida && (
                        <button
                            onClick={() => onMarcarVista(s.id)}
                            className="text-[13px] font-semibold text-rose-700 border border-rose-200 bg-white px-3.5 py-2 rounded-[10px] hover:bg-rose-50 transition-colors"
                        >
                            Marcar vista
                        </button>
                    )}
                    {!yaAdquirida && (
                        <button
                            onClick={() => onDescartar(s)}
                            className="text-[13px] font-semibold text-slate-500 border border-slate-200 bg-white px-3.5 py-2 rounded-[10px] hover:bg-slate-50 transition-colors"
                        >
                            Descartar
                        </button>
                    )}
                    <button
                        onClick={() => onAbrirModal(s)}
                        disabled={yaAdquirida}
                        className={`text-[13px] font-bold px-4 py-2 rounded-[10px] transition-transform ${yaAdquirida
                                ? (isDark ? 'bg-white/10 text-slate-400 cursor-not-allowed' : 'bg-slate-100 text-slate-400 cursor-not-allowed')
                                : 'text-white bg-gradient-to-br from-[#61257B] to-[#C1348B] shadow-lg shadow-purple-900/30 hover:-translate-y-0.5'
                            }`}
                    >
                        {yaAdquirida ? 'Solicitud enviada' : 'Solicitar reserva'}
                    </button>
                </div>
            </div>
        </article>
    );
};

/**
 * Modal de confirmación de reserva.
 */
const ModalConfirmarReserva: React.FC<{
    sugerencia: SugerenciaCita;
    procesando: boolean;
    error: string | null;
    onCancelar: () => void;
    onConfirmar: () => void;
}> = ({ sugerencia: s, procesando, error, onCancelar, onConfirmar }) => {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-reserva-titulo"
            onClick={(e) => {
                if (e.target === e.currentTarget && !procesando) onCancelar();
            }}
        >
            <div className="w-full max-w-[420px] bg-white rounded-[20px] overflow-hidden shadow-[0_24px_60px_rgba(97,37,123,0.25)]">
                {/* Header */}
                <div className="relative bg-gradient-to-br from-[#61257B] to-[#C1348B] px-[22px] py-5 text-white">
                    <div className="text-[11px] font-bold uppercase tracking-[.1em] opacity-85 mb-1">
                        Solicita tu reserva
                    </div>
                    <div id="modal-reserva-titulo" className="text-[17px] font-extrabold pr-8">
                        {s.servicio_sugerido_nombre || 'Servicio sugerido'}
                    </div>
                    <button
                        onClick={onCancelar}
                        disabled={procesando}
                        aria-label="Cerrar"
                        className="absolute top-4 right-4 w-[26px] h-[26px] rounded-lg bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors disabled:opacity-50"
                    >
                        <X className="w-[15px] h-[15px]" strokeWidth={2.2} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-[22px] py-5">
                    <div className="flex flex-col gap-3 mb-[18px]">
                        {s.plan_nombre && (
                            <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600 flex-shrink-0">
                                    <Package className="w-[15px] h-[15px]" strokeWidth={2} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10.5px] text-slate-400 uppercase tracking-[.05em] font-semibold">
                                        Plan
                                    </div>
                                    <div className="text-[13.5px] font-bold text-slate-900">
                                        {s.plan_nombre}
                                    </div>
                                </div>
                            </div>
                        )}

                        {s.fecha_inicio && (
                            <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600 flex-shrink-0">
                                    <CalendarDays className="w-[15px] h-[15px]" strokeWidth={2} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10.5px] text-slate-400 uppercase tracking-[.05em] font-semibold">
                                        Fecha
                                    </div>
                                    <div className="text-[13.5px] font-bold text-slate-900">
                                        {formatFecha(s.fecha_inicio)}
                                        {s.fecha_fin && s.fecha_fin !== s.fecha_inicio
                                            ? ` – ${formatFecha(s.fecha_fin)}`
                                            : ''}
                                    </div>
                                </div>
                            </div>
                        )}

                        {s.hora_inicio && (
                            <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600 flex-shrink-0">
                                    <Clock className="w-[15px] h-[15px]" strokeWidth={2} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10.5px] text-slate-400 uppercase tracking-[.05em] font-semibold">
                                        Horario
                                    </div>
                                    <div className="text-[13.5px] font-bold text-slate-900">
                                        {formatHora(s.hora_inicio)}
                                        {!s.usa_temporizador && s.hora_fin
                                            ? ` - ${formatHora(s.hora_fin)}`
                                            : ''}
                                        {s.usa_temporizador ? ' (con temporizador)' : ''}
                                    </div>
                                </div>
                            </div>
                        )}

                        {s.espacio_servicio_nombre && (
                            <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600 flex-shrink-0">
                                    <MapPin className="w-[15px] h-[15px]" strokeWidth={2} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10.5px] text-slate-400 uppercase tracking-[.05em] font-semibold">
                                        Espacio
                                    </div>
                                    <div className="text-[13.5px] font-bold text-slate-900">
                                        {s.espacio_servicio_nombre}
                                    </div>
                                </div>
                            </div>
                        )}

                        {s.cupon_codigo && (
                            <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600 flex-shrink-0">
                                    <Tag className="w-[15px] h-[15px]" strokeWidth={2} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10.5px] text-slate-400 uppercase tracking-[.05em] font-semibold">
                                        Cupón
                                    </div>
                                    <div className="text-[13.5px] font-bold text-slate-900">
                                        {s.cupon_codigo}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Total */}
                    <div className="bg-gradient-to-br from-rose-800 to-rose-600 rounded-2xl p-4 text-white mb-4">
                        <div className="text-[10.5px] opacity-75 uppercase tracking-[.1em] font-bold mb-1">
                            Total estimado
                        </div>
                        {s.subtotal && s.total && s.subtotal !== s.total && (
                            <div className="text-[10px] opacity-70 line-through mb-0.5">
                                {formatMoneda(s.subtotal)}
                            </div>
                        )}
                        <div className="text-[26px] font-black tracking-tight">
                            {s.total ? formatMoneda(s.total) : 'Consultar'}
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 text-[12.5px] text-red-600 font-medium bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    {/* Acciones */}
                    <div className="flex gap-2.5">
                        <button
                            onClick={onCancelar}
                            disabled={procesando}
                            className="flex-1 py-[11px] rounded-[10px] border-[1.5px] border-purple-200 bg-white text-purple-700 text-[13px] font-bold hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={onConfirmar}
                            disabled={procesando}
                            className="flex-[2] py-[11px] rounded-[10px] border-none bg-gradient-to-br from-[#61257B] to-[#C1348B] text-white text-[13px] font-extrabold shadow-lg shadow-purple-900/30 hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
                        >
                            {procesando ? 'Enviando...' : 'Crear solicitud'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ParaTi: React.FC = () => {
    const { user } = useAuth();
    const { showToast } = useToast();
    const { isDark } = useTheme();
    const identidadActivaRef = useRef({ userId: user?.id, generation: 0 });
    const componenteActivoRef = useRef(true);
    const reservaEnCursoRef = useRef<typeof identidadActivaRef.current | null>(null);
    const siguienteIdentidad = siguienteIdentidadFeed(
        identidadActivaRef.current,
        user?.id,
    );
    if (!identidadFeedCoincide(siguienteIdentidad, identidadActivaRef.current)) {
        reservaEnCursoRef.current = null;
    }
    identidadActivaRef.current = siguienteIdentidad;
    const identidadRender = identidadActivaRef.current;
    const identidadSigueActiva = useCallback(
        (identidad: typeof identidadRender) =>
            componenteActivoRef.current &&
            identidadFeedCoincide(identidad, identidadActivaRef.current),
        []
    );
    const [sugerencias, setSugerencias] = useState<SugerenciaCita[]>([]);
    const [identidadEstado, setIdentidadEstado] = useState(identidadRender);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filtro, setFiltro] = useState<'todas' | 'nuevas' | 'vistas'>('todas');

    // Ids de sugerencias que están en pleno proceso de "salida" animada
    // (descartadas o recién reservadas), pero que todavía siguen en el
    // arreglo `sugerencias` mientras dura la animación.
    const [saliendoIds, setSaliendoIds] = useState<Set<number>>(new Set());

    // Sugerencia seleccionada para mostrar en el modal (null = modal cerrado)
    const [sugerenciaModal, setSugerenciaModal] = useState<SugerenciaCita | null>(null);
    const [reservando, setReservando] = useState(false);
    const [errorModal, setErrorModal] = useState<string | null>(null);

    useEffect(() => {
        // StrictMode repite setup → cleanup → setup sin desmontar realmente el
        // árbol. Un booleano de ciclo de vida se rearma en el segundo setup;
        // una generación monotónica aquí invalidaría también esa carga válida.
        componenteActivoRef.current = true;
        return () => {
            componenteActivoRef.current = false;
            reservaEnCursoRef.current = null;
        };
    }, []);

    useEffect(() => {
        const identidadCarga = identidadRender;
        const usuarioCargadoId = identidadCarga.userId;
        let cancelada = false;
        setIdentidadEstado(identidadCarga);
        setSugerencias([]);
        setSaliendoIds(new Set());
        setSugerenciaModal(null);
        setReservando(false);

        const cargar = async () => {
            if (!usuarioCargadoId) {
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                let data = await fetchSugerenciasByUsuario(usuarioCargadoId);
                if (cancelada || !identidadSigueActiva(identidadCarga)) return;

                // Red de seguridad: si el feed llegó con menos de 3 activas
                // (por ejemplo tras varios descartes/reservas seguidas o
                // reinicios del backend), pedimos que rellene y volvemos
                // a traer la lista actualizada.
                try {
                    const { nuevas_generadas } = await completarSugerencias();
                    if (cancelada || !identidadSigueActiva(identidadCarga)) return;
                    if (nuevas_generadas > 0) {
                        data = await fetchSugerenciasByUsuario(usuarioCargadoId);
                    }
                } catch (err) {
                    /* Browser diagnostics omitted. */
                }

                // El backend persiste primero las opciones de mayor prioridad.
                // Mantener orden ascendente deja cualquier reciclaje recién
                // generado al final también después de recargar la página.
                const ordenadas = [...data].sort(
                    (a, b) =>
                        new Date(a.fecha_generada).getTime() -
                        new Date(b.fecha_generada).getTime()
                );
                if (cancelada || !identidadSigueActiva(identidadCarga)) return;
                setSugerencias(normalizarSugerenciasDiversas(ordenadas));
                setError(null);
            } catch (err) {
                /* Browser diagnostics omitted. */
                if (cancelada || !identidadSigueActiva(identidadCarga)) return;
                setError('No pudimos cargar tus sugerencias. Intenta de nuevo.');
            } finally {
                if (!cancelada && identidadSigueActiva(identidadCarga)) {
                    setLoading(false);
                }
            }
        };
        cargar();
        return () => {
            cancelada = true;
        };
    }, [identidadRender, identidadSigueActiva, user?.id]);

    const marcarVista = async (id: number) => {
        const identidadOperacion = identidadRender;
        if (!identidadSigueActiva(identidadOperacion)) return;
        setSugerencias((prev) =>
            prev.map((s) => (s.id === id ? { ...s, vista: true } : s))
        );
        try {
            await updateSugerencia(id, { vista: true });
        } catch (err) {
            if (!identidadSigueActiva(identidadOperacion)) return;
            /* Browser diagnostics omitted. */
        }
    };

    /**
     * Se llama apenas el IntersectionObserver detecta que la tarjeta fue
     * realmente vista (>=50% visible por primera vez). Solo actualiza el
     * estado LOCAL — la llamada al backend ya la hace el propio hook
     * `useSeguimientoSugerencia` vía `registrarInteraccion(id, {tipo:'impresion'})`.
     * No usamos `updateSugerencia` acá para no duplicar la petición.
     */
    const marcarImpresionLocal = (id: number) => {
        if (!identidadSigueActiva(identidadRender)) return;
        setSugerencias((prev) =>
            prev.map((s) => (s.id === id && !s.vista ? { ...s, vista: true } : s))
        );
    };

    const descartarSugerencia = async (s: SugerenciaCita) => {
        const identidadOperacion = identidadRender;
        const usuarioOperacionId = identidadOperacion.userId;
        if (!identidadSigueActiva(identidadOperacion)) return;
        // 1) Disparamos la animación de salida. La tarjeta sigue en el
        // arreglo (mismo slot del grid) mientras se desvanece — nada más
        // se mueve mientras tanto.
        setSaliendoIds((prev) => new Set(prev).add(s.id));
        await new Promise((resolve) => setTimeout(resolve, DURACION_ANIMACION_SALIDA_MS));

        try {
            if (!identidadSigueActiva(identidadOperacion)) return;
            await registrarInteraccion(s.id, { tipo: 'descartada' });
            // El backend ya generó el/los reemplazo(s) en el mismo request:
            // refrescamos para traerlos, pero en vez de resortear todo el
            // feed, solo insertamos el reemplazo en el slot que dejó esta
            // tarjeta.
            if (usuarioOperacionId) {
                const data = await fetchSugerenciasByUsuario(usuarioOperacionId);
                if (!identidadSigueActiva(identidadOperacion)) return;
                setSugerencias((prev) => fusionarReemplazoDiverso(prev, [s.id], data));
            } else {
                setSugerencias((prev) => prev.filter((item) => item.id !== s.id));
            }
        } catch (err) {
            if (!identidadSigueActiva(identidadOperacion)) return;
            /* Browser diagnostics omitted. */
            showToast({
                type: 'error',
                title: 'No se pudo descartar',
                message: 'Intenta de nuevo en unos segundos.',
            });
            // No se tocó `sugerencias`, así que la tarjeta simplemente
            // vuelve a aparecer (se limpia `saliendoIds` en el finally).
        } finally {
            if (identidadSigueActiva(identidadOperacion)) {
                setSaliendoIds((prev) => {
                    const next = new Set(prev);
                    next.delete(s.id);
                    return next;
                });
            }
        }
    };

    const estadoEsActual = identidadFeedCoincide(
        identidadEstado,
        identidadRender,
    );
    const sugerenciasActuales = useMemo(
        () => (estadoEsActual ? sugerencias : []),
        [estadoEsActual, sugerencias]
    );
    const sugerenciaModalActual = estadoEsActual ? sugerenciaModal : null;
    const saliendoIdsActuales = estadoEsActual ? saliendoIds : new Set<number>();
    const loadingActual = estadoEsActual ? loading : true;
    const errorActual = estadoEsActual ? error : null;

    const sugerenciasFiltradas = useMemo(() => {
        if (filtro === 'nuevas') return sugerenciasActuales.filter((s) => !s.vista);
        if (filtro === 'vistas') return sugerenciasActuales.filter((s) => s.vista);
        return sugerenciasActuales;
    }, [sugerenciasActuales, filtro]);

    const totalNuevas = sugerenciasActuales.filter((s) => !s.vista).length;

    const abrirModalReserva = (s: SugerenciaCita) => {
        if (!identidadSigueActiva(identidadRender)) return;
        if (s.cita_referencia || s.solicitud_reserva) return;
        setErrorModal(null);
        setSugerenciaModal(s);
        registrarInteraccion(s.id, { tipo: 'click_detalle' }).catch((err) =>
            (void 0)
        );
    };

    const cerrarModal = () => {
        if (!identidadSigueActiva(identidadRender)) return;
        if (reservando) return; // no permitir cerrar mientras procesa
        setSugerenciaModal(null);
        setErrorModal(null);
    };

    const confirmarReserva = async () => {
        if (!sugerenciaModalActual) return;
        const identidadOperacion = identidadRender;
        const usuarioOperacionId = identidadOperacion.userId;
        if (!identidadSigueActiva(identidadOperacion)) return;
        if (reservaEnCursoRef.current) return;
        reservaEnCursoRef.current = identidadOperacion;
        setReservando(true);
        setErrorModal(null);
        const idReservada = sugerenciaModalActual.id;
        try {
            const { solicitud } = await reservarSugerencia(idReservada);
            if (!identidadSigueActiva(identidadOperacion)) return;
            setSugerenciaModal(null);

            showToast({
                type: 'success',
                title: 'Solicitud creada',
                message: `Pendiente de validación en POS. Código: ${solicitud.codigo}`,
            });
            if (solicitud.whatsapp_url) {
                window.open(solicitud.whatsapp_url, '_blank', 'noopener,noreferrer');
            }

            // Igual que al descartar: la sugerencia reservada sale del feed
            // (el backend ya generó su/sus reemplazo(s)), pero se inserta
            // en el mismo slot en vez de reordenar todo el grid.
            if (usuarioOperacionId) {
                const data = await fetchSugerenciasByUsuario(usuarioOperacionId);
                if (!identidadSigueActiva(identidadOperacion)) return;
                setSugerencias((prev) => fusionarReemplazoDiverso(prev, [idReservada], data));
            } else {
                setSugerencias((prev) => prev.filter((item) => item.id !== idReservada));
            }
        } catch (err: any) {
            if (!identidadSigueActiva(identidadOperacion)) return;
            const mensaje =
                err instanceof SugerenciaInvalidaError
                    ? err.message
                    : 'No se pudo crear la solicitud. Intenta de nuevo.';
            setErrorModal(mensaje);
        } finally {
            if (
                reservaEnCursoRef.current &&
                identidadFeedCoincide(reservaEnCursoRef.current, identidadOperacion)
            ) {
                reservaEnCursoRef.current = null;
            }
            if (identidadSigueActiva(identidadOperacion)) {
                setReservando(false);
            }
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-6 md:px-10 pb-16 pt-8">
            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
                <div className="max-w-xl">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold tracking-wide ${isDark ? 'text-[#ffd97c] bg-purple-900/40 border border-purple-500/30' : 'text-purple-700 bg-purple-100/80 border border-purple-200'} px-3 py-1 rounded-full mb-3`}>
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                        Para ti
                    </span>
                    <h1 className={`text-2xl md:text-[28px] font-extrabold ${isDark ? 'text-white' : 'text-slate-900'} leading-snug mb-2`}>
                        Sugerencias pensadas para tu próxima cita
                    </h1>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-500'} leading-relaxed`}>
                        El sistema revisa tu historial y tus preferencias para
                        proponerte horarios, servicios y espacios que podrían
                        interesarte.
                    </p>
                </div>

                {/* Tabs */}
                <div className={`flex gap-2 p-1.5 rounded-2xl self-start ${isDark ? 'bg-[rgba(26,12,48,0.8)] border border-purple-500/25' : 'bg-purple-50/60 border border-purple-100'}`}>
                    <button
                        onClick={() => setFiltro('todas')}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${filtro === 'todas'
                            ? 'bg-gradient-to-br from-[#61257B] to-[#C1348B] text-white shadow-lg shadow-purple-900/30'
                            : (isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-purple-700')
                            }`}
                    >
                        Todas
                        <span
                            className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${filtro === 'todas' ? 'bg-white/25' : (isDark ? 'bg-white/10 text-slate-300' : 'bg-black/5')
                                }`}
                        >
                            {sugerenciasActuales.length}
                        </span>
                    </button>

                    <button
                        onClick={() => setFiltro('nuevas')}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${filtro === 'nuevas'
                            ? 'bg-gradient-to-br from-[#61257B] to-[#C1348B] text-white shadow-lg shadow-purple-900/30'
                            : (isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-purple-700')
                            }`}
                    >
                        Nuevas
                        {totalNuevas > 0 && (
                            <span
                                className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${filtro === 'nuevas'
                                    ? 'bg-white/25'
                                    : 'bg-amber-100 text-amber-700'
                                    }`}
                            >
                                {totalNuevas}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => setFiltro('vistas')}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${filtro === 'vistas'
                            ? 'bg-gradient-to-br from-[#61257B] to-[#C1348B] text-white shadow-lg shadow-purple-900/30'
                            : (isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-purple-700')
                            }`}
                    >
                        Vistas
                    </button>
                </div>
            </header>

            {/* Loading */}
            {loadingActual && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {[1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="min-h-[420px] rounded-[20px] bg-gradient-to-r from-rose-50 via-rose-50/40 to-rose-50 bg-[length:400%_100%] animate-pulse"
                        />
                    ))}
                </div>
            )}

            {/* Error */}
            {!loadingActual && errorActual && (
                <div className="text-center py-16 px-5 rounded-[20px] border border-dashed border-red-200 bg-red-50 text-red-700">
                    <p className="text-sm">{errorActual}</p>
                </div>
            )}

            {/* Empty state */}
            {!loadingActual && !errorActual && sugerenciasFiltradas.length === 0 && (
                <div className="text-center py-16 px-5 rounded-[20px] border border-dashed border-rose-200 bg-rose-50/40">
                    <Sparkles className="w-6 h-6 text-rose-600 mx-auto mb-2" strokeWidth={1.75} />
                    <h3 className="text-base font-bold text-slate-900 mb-1">
                        Estamos buscando nuevas opciones para ti
                    </h3>
                    <p className="text-sm text-slate-400">
                        En cuanto exista un servicio con espacio y horario disponibles,
                        aparecerá aquí automáticamente.
                    </p>
                </div>
            )}

            {/* Grid de tarjetas */}
            {!loadingActual && !errorActual && sugerenciasFiltradas.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {sugerenciasFiltradas.map((s) => (
                        <TarjetaSugerencia
                            key={s.id}
                            s={s}
                            saliendo={saliendoIdsActuales.has(s.id)}
                            onMarcarVista={marcarVista}
                            onImpresion={marcarImpresionLocal}
                            onAbrirModal={abrirModalReserva}
                            onDescartar={descartarSugerencia}
                        />
                    ))}
                </div>
            )}

            {/* Modal de confirmación */}
            {sugerenciaModalActual && (
                <ModalConfirmarReserva
                    sugerencia={sugerenciaModalActual}
                    procesando={estadoEsActual && reservando}
                    error={estadoEsActual ? errorModal : null}
                    onCancelar={cerrarModal}
                    onConfirmar={confirmarReserva}
                />
            )}
        </div>
    );
};

export default ParaTi;
